import useAppStore from "@/context/store";
import { Checkbox, message, Modal } from "antd";
import styles from "./index.module.less";

// 常量定义
const AI_CONTENT_TEXT = "作品含 AI 生成内容，是否保留 AI 显式标识？";
const AI_IMPLICIT_TEXT =
  "根据《人工智能生成合成内容标识办法》，花瓣已对（含）AI 生成内容添加显式标识及隐式标识，其中显式标识可以申请去除。后续您通过互联网传播由花瓣生成的 AI 内容时，应保持上述内容标识的准确性、完整性。网络传播平台在获知或检测到您上传内容为 AI 生成内容时会在您发布的内容周围添加显著提示，详情可查阅";
const AGREEMENT_CONFIRMATION_TEXT = "请阅读并同意相关协议";
const AGREEMENT_NAME = "user_ai_tag_agreement";

// 用户协议地址
const AGREEMENT_URL = "https://huaban.com/about/disclaimer?activeKey=0";

/**
 * 检查用户是否已同意过协议
 */
function hasShownAgreement(userId?: number): boolean {
  try {
    if (!userId) {
      return false;
    }
    const agreementKey = `${AGREEMENT_NAME}_${userId}`;
    const userAgreement = localStorage.getItem(agreementKey);
    return userAgreement === "1";
  } catch {
    return false;
  }
}

/**
 * 更新用户协议状态
 */
function updateUserAgreement(userId: number | undefined, agreed: boolean) {
  try {
    if (!userId) {
      return;
    }
    const agreementKey = `${AGREEMENT_NAME}_${userId}`;
    localStorage.setItem(agreementKey, agreed ? "1" : "0");
  } catch {}
}

/**
 * AI水印确认弹窗
 * @returns Promise<boolean> true-去除水印, false-保留水印
 */
export function showAIWatermarkDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const user = useAppStore.getState().user;
    const userId = user?.user_id;
    const hasShown = hasShownAgreement(userId);

    let agreed = hasShown;

    const handleAction = (shouldRemoveWatermark: boolean) => {
      if (!agreed) {
        message.warning(AGREEMENT_CONFIRMATION_TEXT);
        return Promise.reject(); // 阻止关闭弹窗
      }
      resolve(shouldRemoveWatermark);
      return Promise.resolve();
    };

    Modal.confirm({
      title: AI_CONTENT_TEXT,
      width: 480,
      className: styles.aiWatermarkModal,
      icon: null,
      maskClosable: false,
      closable: false,
      okText: "去除",
      cancelText: "保留",
      okButtonProps: { size: "large" },
      cancelButtonProps: { size: "large" },
      content: (
        <div className={styles.content}>
          <div className={styles.secondaryText}>
            {AI_IMPLICIT_TEXT}
            <a
              href={AGREEMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              《用户协议》
            </a>
          </div>
          {!hasShown && (
            <div>
              <Checkbox
                defaultChecked={false}
                onChange={(e) => {
                  agreed = e.target.checked;
                  updateUserAgreement(userId, agreed);
                }}
              >
                我已阅读并同意
              </Checkbox>
              <a
                href={AGREEMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                《用户协议》
              </a>
            </div>
          )}
        </div>
      ),
      onOk: () => handleAction(true),
      onCancel: () => handleAction(false),
    });
  });
}

/**
 * 展示带水印确认的导出对话框
 * @param callbackFun 回调函数，参数为是否绘制水印
 * @returns 回调函数的返回值
 */
export async function showExportWithAIWatermarkDialog<T>(
  callbackFun: (isDrawAIWatermark: boolean) => Promise<T>
): Promise<T> {
  const shouldRemoveWatermark = await showAIWatermarkDialog();
  // 用户选择"去除"，则 isDrawAIWatermark 为 false
  const result = await callbackFun(!shouldRemoveWatermark);
  return result;
}
